import {memo, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import * as assignableSelectors from 'nutshell-core/assignables/assignables-selectors';

import {AiCallout} from 'shells/ai-callout/ai-callout';
import {AssigneeSelect} from 'shells/assignee-select/assignee-select';
import {FormField} from 'shells/form/form-field';
import {LeadChevronRegularIcon} from 'shells/icon/svg/lead-chevron-regular-icon';
import {getContactDisplayName, getRecipientFromThread} from 'shells/inbox/helpers';
import {Stack} from 'shells/layout/stack/stack';
import {Body} from 'shells/typography/body';

import {ThreadType} from 'nutshell-graphql-types';
import type {InboxThreadFragment} from 'nutshell-graphql-types';

import {
    AUTO_ASSIGN_OPTION_COMPACT,
    AUTO_DISTRIBUTE_OPTION,
    AUTO_RESOLVE_ID,
} from '../../../core/form/fields/consts';
import {PipelineSelectPicker} from '../../../core/form/fields/pipeline-select-picker';
import {useGetSessionUser} from '../../../core/use-get-session-user';

import type {NutshellState} from '../../../../store';

import {AddLeadFromContactButton} from '../add-lead-from-contact-button';
import {AddLeadFromThreadButton} from '../add-lead-from-thread-button';
import {getOriginFromThreadType} from '../helpers';

interface Props {
    thread: InboxThreadFragment;
}

export const LeadSidebarEmptyState = memo(function LeadSidebarEmptyState(props: Props) {
    const [selectedStageset, setSelectedStageset] = useState<string>(AUTO_DISTRIBUTE_OPTION.value);
    const [selectedAssignee, setSelectedAssignee] = useState<string | null>(
        () => props.thread.owner?.id ?? AUTO_RESOLVE_ID
    );
    const {user} = useGetSessionUser();

    useEffect(() => {
        setSelectedAssignee(props.thread.owner?.id ?? AUTO_RESOLVE_ID);
    }, [props.thread]);

    const isAssigneeSelectDisabled = Boolean(user && !user.permissions.canAssignEntities);
    const activeAssignables =
        useSelector((state: NutshellState) => assignableSelectors.getAllEnabled(state)) ?? [];

    const assigneeIdForCreate = selectedAssignee === AUTO_RESOLVE_ID ? null : selectedAssignee;

    const isEmailThread = props.thread.threadType === ThreadType.EMAIL;

    const recipient = getRecipientFromThread(props.thread);
    const contactDisplayName = getContactDisplayName(props.thread, recipient);

    return (
        <div className='flex flex-dir-col pad-16 full-height justify-sb'>
            <Stack spacing={16}>
                <Body size='small' isBold={true} truncate={{type: 'inline'}}>
                    Create a lead for {recipient.name}?
                </Body>
                <div className='flex flex-dir-col gap-16 border pad-16 br-3'>
                    <Stack isHorizontal={true} spacing={8} alignItems='center'>
                        <LeadChevronRegularIcon size={16} wrapWithDiv={false} />
                        <Body isBold={true} size='small' truncate={{type: 'block', maxLines: 1}}>
                            {contactDisplayName}
                        </Body>
                    </Stack>
                    <FormField innerId='lead-pipeline-select' label='Pipeline' noMargin={true}>
                        <PipelineSelectPicker
                            selectedPipeline={selectedStageset}
                            onSelectPipeline={setSelectedStageset}
                            menuPlacement='auto'
                            useAutoDistribute={true}
                        />
                    </FormField>
                    <FormField innerId='lead-assignee-select' label='Assignee' noMargin={true}>
                        <AssigneeSelect
                            customOptions={[AUTO_ASSIGN_OPTION_COMPACT]}
                            options={activeAssignables}
                            value={selectedAssignee}
                            onChange={(id) => setSelectedAssignee(id ?? AUTO_RESOLVE_ID)}
                            isDisabled={isAssigneeSelectDisabled}
                            menuPlacement='auto'
                            hideDividerWhenEmpty={true}
                            withControlIcon={true}
                        />
                    </FormField>
                </div>
                {isEmailThread ? (
                    <AddLeadFromThreadButton
                        recipientId={recipient.id}
                        threadId={props.thread.id}
                        threadType={props.thread.threadType}
                        assigneeId={assigneeIdForCreate}
                        stagesetId={selectedStageset}
                    />
                ) : (
                    <AddLeadFromContactButton
                        recipientId={recipient.id}
                        threadId={props.thread.id}
                        origin={getOriginFromThreadType(props.thread.threadType)}
                        stagesetId={selectedStageset}
                        assigneeId={assigneeIdForCreate}
                    />
                )}
            </Stack>
            {isEmailThread && (
                <AiCallout
                    title='AI lead creation'
                    content={
                        <Body size='small'>
                            Automatically create a lead using details from their email signature and
                            domain.
                        </Body>
                    }
                />
            )}
        </div>
    );
});
